import { showToast } from "./toast";

/**
 * User-friendly copy for API error codes (settings, access control, booking gates).
 * Prefer these over raw backend messages in toasts and forms.
 */
export const USER_FACING_ERRORS = {
  MAINTENANCE_MODE: {
    message:
      "NEMA Care is under maintenance right now. Please try again shortly—you can still sign in as admin if you have access.",
    toastType: "info",
  },
  REGISTRATION_DISABLED: {
    message:
      "New account registration is paused. If you already have an account, sign in instead, or contact support for help.",
    toastType: "info",
  },
  CAREGIVER_REGISTRATION_DISABLED: {
    message:
      "Caregiver self-registration is turned off. Please contact NEMA Care to join as a caregiver, or sign in if you already have an account.",
    toastType: "info",
  },
  RATINGS_DISABLED: {
    message: "Ratings and reviews are temporarily unavailable on the platform.",
    toastType: "info",
  },
  PATIENT_SUBSCRIPTION_REQUIRED: {
    message:
      "An active Care Pass or subscription is required for this patient before you can book. Choose a plan to continue.",
    toastType: "info",
  },
  CUSTOMER_SUBSCRIPTION_REQUIRED: {
    message: "A customer subscription is required to book this service. Visit Billing & Care Passes to subscribe.",
    toastType: "info",
  },
  COMMITMENT_FEE_UNPAID: {
    message:
      "The commitment fee for full-time care must be paid before you can book. Open Billing & Care Passes to complete payment.",
    toastType: "info",
  },
  CUSTOMER_KYC_REQUIRED: {
    message:
      "Complete Aadhaar KYC and wait for admin approval before booking or using the marketplace.",
    toastType: "info",
  },
  SUBSCRIPTION_UNPAID_EXTENDED: {
    message: "Your account has an unpaid subscription block. Please resolve billing in Billing & Care Passes.",
    toastType: "error",
  },
  CARE_PASS_TYPE_MISMATCH: {
    message: "This Care Pass does not apply to the shift you selected. Choose a matching plan or change the shift.",
    toastType: "info",
  },
  CARE_PASS_DAYS_EXHAUSTED: {
    message: "This patient's Care Pass has no remaining days for this booking. Renew or upgrade in Billing & Care Passes.",
    toastType: "info",
  },
  CARE_PASS_BOOKINGS_EXHAUSTED: {
    message: "This patient's Care Pass has no remaining bookings. Renew or upgrade in Billing & Care Passes.",
    toastType: "info",
  },
  SERVICE_COMING_SOON: {
    message: "This service is not available for booking yet. Try another service or check back later.",
    toastType: "info",
  },
};

export function isKnownUserFacingError(err) {
  const code = String(err?.code || err?.body?.code || "").toUpperCase();
  if (USER_FACING_ERRORS[code]) return true;
  if (Number(err?.status) === 503) return true;
  return false;
}

export function resolveMessageFromApiBody(data, status) {
  if (!data || typeof data !== "object") return null;
  const code = String(data.code || "").toUpperCase();
  const mapped = USER_FACING_ERRORS[code];
  if (mapped?.message) return mapped.message;
  if (status === 503 && /maintenance/i.test(String(data.message || ""))) {
    return USER_FACING_ERRORS.MAINTENANCE_MODE.message;
  }
  return null;
}

export function formatUserFacingError(err, fallback = "Something went wrong. Please try again.") {
  const code = String(err?.code || err?.body?.code || "").toUpperCase();
  const mapped = USER_FACING_ERRORS[code];
  if (mapped?.message) return mapped.message;
  const raw = err?.body?.message || err?.message;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  return fallback;
}

export function toastTypeForError(err) {
  const code = String(err?.code || err?.body?.code || "").toUpperCase();
  const mapped = USER_FACING_ERRORS[code];
  if (mapped?.toastType) return mapped.toastType;
  if (Number(err?.status) === 503) return "info";
  return "error";
}

export function notifyUserFacingError(err, fallback) {
  const message = formatUserFacingError(err, fallback);
  const type = toastTypeForError(err);
  if (type === "info") showToast.info(message);
  else if (type === "success") showToast.success(message);
  else showToast.error(message);
  return message;
}

export function isSubscriptionRequiredError(err) {
  const code = String(err?.body?.code || err?.code || "").toUpperCase();
  return (
    Number(err?.status) === 402 ||
    code === "CUSTOMER_SUBSCRIPTION_REQUIRED" ||
    code === "PATIENT_SUBSCRIPTION_REQUIRED"
  );
}
